import React from "react";
import { api, PROPERTY_TYPES } from "../lib/api";
import { toast } from "sonner";

function describeCombo(c) {
  const parts = [];
  if (c.emirate) parts.push(c.emirate);
  if (c.zone) parts.push(c.zone);
  if (c.property_type) {
    const pt = PROPERTY_TYPES.find((p) => p.value === c.property_type);
    parts.push(pt?.label || c.property_type);
  }
  if (c.rent_sale) parts.push(c.rent_sale.toUpperCase());
  return parts.join(" · ") || "Any";
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-zinc-200 p-5">
      <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mb-1">{label}</div>
      <div className="font-display text-3xl tracking-tighter">{value}</div>
      {sub && <div className="font-mono-tech text-[10px] text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

function RankedList({ title, items, labelKey = "value", emptyText = "No data yet" }) {
  const max = items[0]?.count || 0;
  return (
    <div className="bg-white border border-zinc-200">
      <div className="font-mono-tech text-[10px] uppercase text-zinc-500 px-4 py-3 border-b border-zinc-100">
        // {title}
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center font-mono-tech text-xs text-zinc-400">{emptyText}</div>
      ) : (
        <ul>
          {items.map((it, i) => (
            <li key={`${it[labelKey]}-${i}`} className="px-4 py-2.5 border-b border-zinc-100 last:border-0">
              <div className="flex items-baseline justify-between gap-3 mb-1">
                <span className="text-sm">{it[labelKey] || "—"}</span>
                <span className="font-mono-tech text-xs text-zinc-500 tabular-nums">{it.count}</span>
              </div>
              <div className="h-1 bg-zinc-100 overflow-hidden">
                <div
                  className="h-full bg-[#031833]"
                  style={{ width: `${max ? (it.count / max) * 100 : 0}%` }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function AdminInsights() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const fetchData = React.useCallback(() => {
    api.get("/admin/analytics/search").then((r) => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  React.useEffect(fetchData, [fetchData]);

  const reset = async () => {
    if (!window.confirm("Wipe all analytics data? This cannot be undone.")) return;
    await api.delete("/admin/analytics/search");
    toast.success("Analytics reset");
    fetchData();
  };

  if (loading) return <div className="font-mono-tech text-xs text-zinc-500">Loading…</div>;
  if (!data) return <div className="font-mono-tech text-xs text-red-600">Failed to load insights</div>;

  return (
    <div data-testid="admin-insights">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-1">// SEARCH INSIGHTS</div>
          <h1 className="font-display text-4xl tracking-tighter">DEMAND SIGNALS</h1>
          <p className="font-mono-tech text-xs text-zinc-500 mt-2 max-w-xl">
            Aggregate, anonymous filter usage from public visitors. No PII captured —
            only which facets are being applied. Use this to guide acquisition priorities.
          </p>
        </div>
        <button
          data-testid="reset-insights-btn"
          onClick={reset}
          className="font-mono-tech text-[10px] uppercase border border-zinc-300 text-zinc-600 px-3 py-2 hover:border-red-600 hover:text-red-600"
        >
          Reset all
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Filter applies" value={data.total_applies.toLocaleString()} sub="non-empty filter combos" />
        <StatCard label="Saved searches" value={data.total_saves.toLocaleString()} sub="user-triggered saves" />
        <StatCard label="Top zone" value={data.top_zones[0]?.value || "—"} sub={data.top_zones[0] ? `${data.top_zones[0].count} applies` : null} />
        <StatCard label="Top type" value={(PROPERTY_TYPES.find(p => p.value === data.top_property_types[0]?.value) || {}).label || data.top_property_types[0]?.value || "—"} sub={data.top_property_types[0] ? `${data.top_property_types[0].count} applies` : null} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-8">
        <RankedList title="Most-filtered zones" items={data.top_zones} />
        <RankedList title="Most-filtered property types" items={data.top_property_types.map(t => ({...t, value: (PROPERTY_TYPES.find(p => p.value === t.value) || {}).label || t.value}))} />
        <RankedList title="Emirate demand" items={data.top_emirates} />
        <RankedList title="Rent vs. sale" items={data.top_rent_sale.map(r => ({...r, value: r.value.toUpperCase()}))} />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-zinc-200">
          <div className="font-mono-tech text-[10px] uppercase text-zinc-500 px-4 py-3 border-b border-zinc-100">
            // Top filter combinations
          </div>
          {data.top_apply_combos.length === 0 ? (
            <div className="p-6 text-center font-mono-tech text-xs text-zinc-400">No data yet</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="font-mono-tech text-[10px] uppercase text-zinc-500">
                  <th className="text-left p-3">Combination</th>
                  <th className="text-right p-3">Applies</th>
                </tr>
              </thead>
              <tbody>
                {data.top_apply_combos.map((c, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="p-3">{describeCombo(c)}</td>
                    <td className="p-3 text-right font-mono-tech text-xs tabular-nums">{c.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white border border-zinc-200">
          <div className="font-mono-tech text-[10px] uppercase text-zinc-500 px-4 py-3 border-b border-zinc-100">
            // Most-saved searches
          </div>
          {data.top_save_combos.length === 0 ? (
            <div className="p-6 text-center font-mono-tech text-xs text-zinc-400">
              No saved searches recorded yet
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="font-mono-tech text-[10px] uppercase text-zinc-500">
                  <th className="text-left p-3">Combination</th>
                  <th className="text-right p-3">Saves</th>
                </tr>
              </thead>
              <tbody>
                {data.top_save_combos.map((c, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="p-3">{describeCombo(c)}</td>
                    <td className="p-3 text-right font-mono-tech text-xs tabular-nums">{c.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
